import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, FormControl, InputLabel, List, ListItem, MenuItem, Pagination, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Navbar from '../../components/Navbar/Navbar';
import IELTSWorkspaceSidebar, { PageShell, DashboardContainer } from '../../components/IELTS/IELTSWorkspaceSidebar';
import { IeltsMiniRequest } from '../../Client/request';
import MiniExercisePlayer from './MiniExercisePlayer';
import { isMiniDraft } from './useMiniAttempt';
import { categoryLabels, miniReviewDetail } from './miniContent';
import { getSectionColor } from '../../components/IELTS/styles/theme';
import { ListeningIcon, ReadingIcon, WritingIcon, SpeakingIcon } from '../../components/IELTS/icons';
import '../../pages/IELTSDashboardV2.css';

const modules = ['Listening', 'Reading', 'Writing', 'Speaking'];
const moduleIcons = { Listening: ListeningIcon, Reading: ReadingIcon, Writing: WritingIcon, Speaking: SpeakingIcon };

export default function MiniExercises({ historyOnly = false, onHistorySummary }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const module = modules.includes(params.get('module')) ? params.get('module') : 'Reading';
  const history = historyOnly;
  const requestedPage = Number(params.get(history ? 'resultsPage' : 'page'));
  const page = Number.isSafeInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
  const attemptId = history ? null : params.get('attempt');
  const [catalogue, setCatalogue] = useState(null);
  const [list, setList] = useState(null);
  const [active, setActive] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [reload, setReload] = useState(0);
  const [testType, setTestType] = useState(() => localStorage.getItem('ielts-type') === 'academic' ? 'academic' : 'general');
  const startLock = useRef(false);
  const task = module === 'Writing' && ['1', '2'].includes(params.get('task')) ? params.get('task') : 'all';
  const moduleCategories = (catalogue?.categories || []).filter(item => item.module === module);
  const categories = moduleCategories.filter(item => task === 'all' || String(item.task_number) === task);
  const category = categories.some(item => item.category === params.get('category')) ? params.get('category') : 'all';

  const update = changes => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([key, value]) => {
      const name = history && key === 'page' ? 'resultsPage' : key;
      value ? next.set(name, String(value)) : next.delete(name);
    });
    setParams(next);
  };

  useEffect(() => {
    if (history) return;
    const changeType = event => {
      const value = event.detail?.ieltsType;
      if (!['academic', 'general'].includes(value)) return;
      setTestType(value);
      setParams(previous => {
        const next = new URLSearchParams(previous);
        next.delete('page'); next.delete('category');
        return next;
      }, { replace: true });
    };
    window.addEventListener('ieltsTypeChanged', changeType);
    return () => window.removeEventListener('ieltsTypeChanged', changeType);
  }, [history, setParams]);

  useEffect(() => {
    let cancelled = false;
    setError('');
    setCatalogue(null);
    IeltsMiniRequest(testType === 'general' ? '/categories?testType=general' : '/categories')
      .then(data => { if (!cancelled) setCatalogue({ ...data, testType }); })
      .catch(err => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [reload, testType]);

  useEffect(() => {
    if (!catalogue || attemptId || (!history && (!category || catalogue.testType !== testType))) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    setList(null);
    const path = history ? `/history?page=${page}` : `/?${new URLSearchParams({ module, ...(testType === 'general' ? { testType } : {}), ...(task === 'all' ? {} : { task }), ...(category === 'all' ? {} : { category }), page })}`;
    IeltsMiniRequest(path).then(data => {
      if (cancelled) return;
      if (data.paid === false) setCatalogue(previous => ({ ...previous, paid: false }));
      setList(data);
      if (history) onHistorySummary?.(data.summary || null);
    }).catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [catalogue?.paid, module, task, category, page, history, attemptId, reload, onHistorySummary, testType]);

  useEffect(() => {
    if (!attemptId) { setActive(null); return; }
    if (!catalogue || String(active?.attempt.id) === attemptId) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    IeltsMiniRequest(`/attempts/${encodeURIComponent(attemptId)}`).then(async attempt => {
      const review = miniReviewDetail(attempt);
      if (!review && !catalogue.paid) throw new Error('An active subscription is required to continue this exercise.');
      const detail = review || await IeltsMiniRequest(`/${encodeURIComponent(attempt.exercise_id)}`);
      if (!cancelled) setActive({ detail, attempt });
    }).catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [attemptId, catalogue?.paid, active?.attempt.id, reload]);

  const start = async item => {
    if (startLock.current || !catalogue?.paid) return;
    startLock.current = true;
    setStarting(true);
    setError('');
    try {
      const detail = await IeltsMiniRequest(`/${encodeURIComponent(item.id)}`);
      const attempt = await IeltsMiniRequest(`/${encodeURIComponent(item.id)}/attempts`, 'POST');
      setActive({ detail, attempt });
      update({ attempt: attempt.id });
    } catch (err) { setError(err.message); }
    finally { startLock.current = false; setStarting(false); }
  };

  const exit = () => {
    if (params.get('from') === 'results') {
      navigate(`/ielts-dashboard?view=results&resultType=mini&resultsPage=${Number(params.get('resultsPage')) || 1}`);
      return;
    }
    setActive(null); update({ attempt: null }); setReload(value => value + 1);
  };
  if (!history && params.get('view') === 'history' && !attemptId) return <Navigate replace to="/ielts-dashboard?view=results&resultType=mini" />;
  if (active && String(active.attempt.id) === attemptId && (catalogue?.paid || active.attempt.status === 'completed')) return <MiniExercisePlayer key={active.attempt.id} detail={active.detail} initialAttempt={active.attempt} onExit={exit} exitLabel={params.get('from') === 'results' ? 'Results' : 'Mini Exercises'} />;

  const content = <Box component={history ? 'div' : 'main'} className={history ? undefined : 'ielts-sectional-catalog'} sx={{ minWidth: 0, color: '#575c68', '& .MuiButton-root': { textTransform: 'none' } }}>
          {!history && <Typography component="h1" variant="h5" sx={{ fontWeight: 700, color: '#29213c', fontSize: 30, lineHeight: 1.4, mt: 1, mb: 3 }}>Mini Exercises</Typography>}
          {error && <Alert severity="error" action={<Button onClick={() => setReload(value => value + 1)}>Retry</Button>} sx={{ mb: 2 }}>{error}</Alert>}
          {!catalogue && !error && <CircularProgress aria-label="Loading mini exercises" />}
          {catalogue && <>
            {!history && <>
              <div className="ielts-sectional-skills" role="group" aria-label="Exercise skill">
                {modules.map(value => {
                  const Icon = moduleIcons[value];
                  const count = (catalogue.categories || []).filter(item => item.module === value).reduce((total, item) => total + Number(item.count || 0), 0);
                  return <button type="button" key={value} className={`ielts-sectional-skill ${value.toLowerCase()}`}
                    aria-pressed={module === value} onClick={() => update({ module: value, task: null, category: null, page: null, attempt: null })}>
                    <span className="ielts-sectional-skill-icon"><Icon sx={{ fontSize: 22 }} /></span>
                    <span><strong>{value}</strong><small>{count} exercises</small></span>
                  </button>;
                })}
              </div>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              {module === 'Writing' && <FormControl size="small" sx={{ width: { xs: '100%', sm: 160 } }}>
                <InputLabel id="mini-task-label">Task</InputLabel>
                <Select labelId="mini-task-label" label="Task" value={task} onChange={event => {
                  const value = event.target.value;
                  update({ task: value === 'all' ? null : value, page: null,
                    category: category !== 'all' && moduleCategories.some(item => item.category === category
                      && (value === 'all' || String(item.task_number) === value)) ? category : null });
                }}>
                  <MenuItem value="all">All tasks</MenuItem>
                  <MenuItem value="1">Task 1</MenuItem>
                  <MenuItem value="2">Task 2</MenuItem>
                </Select>
              </FormControl>}
              {categories.length > 0 && <FormControl size="small" sx={{ width: { xs: '100%', sm: 320 }, maxWidth: '100%' }}>
                <InputLabel id="mini-category-label">Category</InputLabel>
                <Select labelId="mini-category-label" label="Category" value={category} onChange={event => update({ category: event.target.value === 'all' ? null : event.target.value, page: null })}>
                  <MenuItem value="all">All categories</MenuItem>
                  {categories.map(item => <MenuItem key={item.category} value={item.category}>{categoryLabels[item.category] || item.category} ({item.count})</MenuItem>)}
                </Select>
              </FormControl>}
              </Box>
            </>}
            {loading ? <CircularProgress aria-label="Loading exercises" /> : attemptId ? <Button onClick={exit}>Back to Mini Exercises</Button> : <>
              {history ? <TableContainer sx={{ mb: 3 }}><Table aria-label="Mini exercise results">
                <TableHead sx={{ '& .MuiTableCell-root': { fontWeight: 600 } }}><TableRow>
                  {['Test Name', 'Date', 'Type', 'Status', 'Actions'].map(label => <TableCell key={label}>{label}</TableCell>)}
                </TableRow></TableHead>
                <TableBody>
                  {(list?.items || []).map(item => {
                    const available = catalogue.paid || item.status === 'completed';
                    const url = `/ielts-mini-exercises?attempt=${encodeURIComponent(item.id)}&from=results&resultsPage=${page}`;
                    const color = getSectionColor(item.module);
                    return <TableRow key={item.id} hover tabIndex={available ? 0 : undefined}
                      sx={{ cursor: available ? 'pointer' : 'default' }}
                      onClick={() => { if (available) navigate(url); }}
                      onKeyDown={event => {
                        if (event.target === event.currentTarget && available && ['Enter', ' '].includes(event.key)) {
                          event.preventDefault(); navigate(url);
                        }
                      }}>
                      <TableCell>
                        <Typography variant="body1">{item.title || item.exercise_title || `Exercise ${item.exercise_id || item.id}`}</Typography>
                        <Typography variant="caption" color="text.secondary">Mini Exercise · {categoryLabels[item.category] || item.module}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{item.updated_at ? new Date(item.updated_at).toLocaleDateString() : 'N/A'}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.updated_at ? new Date(item.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</Typography>
                      </TableCell>
                      <TableCell><Chip size="small" label={item.module} sx={{ minWidth: 80, bgcolor: color.primary, color: 'white', fontWeight: 600 }} /></TableCell>
                      <TableCell><Chip size="small" label={item.status.replace(/_/g, ' ').toUpperCase()}
                        color={({ completed: 'success', draft: 'info', evaluating: 'warning', failed: 'error' })[item.status] || 'default'} /></TableCell>
                      <TableCell onClick={event => event.stopPropagation()}>
                        <Button variant="outlined" startIcon={<PlayArrowIcon />} disabled={!available}
                          {...(available ? { component: Link, to: url } : {})}>
                          {isMiniDraft(item.status) ? 'Resume' : 'View'}
                        </Button>
                      </TableCell>
                    </TableRow>;
                  })}
                  {list && !list.items?.length && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}>No mini exercise attempts yet.</TableCell></TableRow>}
                </TableBody>
              </Table></TableContainer> : <Box className="ielts-sectional-group">
                {category && <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 650, color: '#29213c' }}>{category === 'all' ? `All ${module} exercises` : categoryLabels[category] || category}</Typography>
                    <Typography variant="caption" color="text.secondary">{list?.total || 0} exercises</Typography>
                  </Box>
                  <Chip label={module} size="small" className={`ielts-sectional-tag ${module.toLowerCase()}`} />
                </Box>}
                <List disablePadding sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: '12px', mt: '12px' }}>
                {(list?.items || []).map(item => {
                  const name = item.module === 'Speaking'
                    ? ({ 'part-1': 'Interview', 'part-2': 'Cue Card', 'part-3': 'Discussion' })[item.category] || 'Speaking Practice'
                    : categoryLabels[item.category] || 'Practice Exercise';
                  const numbers = Array.isArray(item.question_numbers) ? item.question_numbers : [];
                  let scope = '';
                  if (numbers.length) {
                    const consecutive = numbers.length > 1 && numbers.every((number, index) => Number(number) === Number(numbers[0]) + index);
                    scope = `${numbers.length === 1 ? 'Question' : 'Questions'} ${consecutive ? `${numbers[0]}–${numbers[numbers.length - 1]}` : numbers.join(', ')}`;
                  } else if (item.question_count && item.module !== 'Writing') {
                    const unit = item.module === 'Speaking' ? 'prompt' : 'question';
                    scope = `${item.question_count} ${unit}${Number(item.question_count) === 1 ? '' : 's'}`;
                  }
                  return <ListItem key={item.id} className="ielts-sectional-test-card" sx={{ display: 'flex' }}>
                  <Box className="ielts-sectional-test-info" sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#2D3748', mb: '4px', overflowWrap: 'anywhere' }}>{name}{item.part_number ? ` · ${item.module === 'Writing' ? 'Task' : 'Part'} ${item.part_number}` : ''}</Typography>
                    <Typography variant="body2" color="text.secondary">{item.test_title || item.module}</Typography>
                    {scope && <Typography variant="caption" color="text.secondary">{scope}</Typography>}
                    {item.attempted && <Chip size="small" label="Attempted" sx={{ mt: 1 }} />}
                  </Box>
                  <Box className="ielts-sectional-test-actions">
                  <Button variant="contained" startIcon={catalogue.paid === false ? <LockOutlinedIcon /> : starting ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />} disabled={starting || catalogue.paid === false} onClick={() => start(item)}>
                    {catalogue.paid === false ? 'Upgrade' : 'Start'}
                  </Button>
                  </Box>
                </ListItem>;
                })}
              </List></Box>}
              {!history && list && !list.items?.length && <Typography sx={{ py: 4 }} color="text.secondary">{category === 'all' ? `No exercises available for ${module}.` : 'No exercises in this category yet.'}</Typography>}
              {!history && !category && <Typography color="text.secondary">No categories available for {module}.</Typography>}
              {Number(list?.total) > Number(list?.pageSize || 20) && <Pagination sx={{ mt: 3 }} page={page} count={Math.ceil(list.total / (list.pageSize || 20))} onChange={(_, value) => update({ page: value })} />}
            </>}
          </>}
        </Box>;
  if (history) return content;
  return <Box><Navbar /><PageShell><DashboardContainer maxWidth="xl">
    <IELTSWorkspaceSidebar activeView="mini" isSubscribed={catalogue?.paid !== false} />
    {content}
  </DashboardContainer></PageShell></Box>;
}
